import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { AuthApiError, confirmPasswordReset } from '../../auth/authApi'
import type { AuthResponse } from '../../auth/types'
import { Button, Field, Icon, I } from '../../components/ui'
import { AuthLayout } from './AuthLayout'
import { passwordMeetsPolicy, passwordPolicyChecks, type PasswordPolicyCheck } from './passwordPolicy'

interface ResetPasswordPageProps {
  token: string | null
  onResetSuccess: (response: AuthResponse) => void
  onRequestNewLink: () => void
}

function PasswordChecklist({ checks }: { checks: PasswordPolicyCheck[] }) {
  const { t } = useTranslation()
  return (
    <div style={{ display: 'grid', gap: 7, padding: 12, borderRadius: 12, background: 'var(--ink-50)', border: '1px solid var(--border-subtle)' }}>
      {checks.map(check => (
        <div key={check.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: check.ok ? 'var(--success)' : 'var(--fg-subtle)', fontWeight: check.ok ? 700 : 500 }}>
          <Icon size={14} color={check.ok ? 'var(--success)' : 'var(--fg-disabled)'}>{check.ok ? I.check : I.clock}</Icon>
          {t(`auth.policy.${check.key}`)}
        </div>
      ))}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--fg-subtle)', fontWeight: 500 }}>
        <Icon size={14} color="var(--fg-disabled)">{I.clock}</Icon>
        {t('auth.policy.noPersonal')}
      </div>
    </div>
  )
}

function InvalidTokenState({ onRequestNewLink }: { onRequestNewLink: () => void }) {
  const { t } = useTranslation()
  return (
    <AuthLayout
      eyebrow={t('auth.reset.invalidEyebrow')}
      title={t('auth.reset.invalidTitle')}
      subtitle={t('auth.reset.invalidSubtitle')}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button type="button" onClick={onRequestNewLink} style={{ border: 'none', background: 'transparent', color: 'var(--brand-700)', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
            {t('auth.reset.requestNew')}
          </button>
        </div>
      )}
    >
      <div style={{ display: 'grid', gap: 18 }}>
        <div style={{
          width: 52,
          height: 52,
          borderRadius: 18,
          display: 'grid',
          placeItems: 'center',
          background: 'var(--danger-100)',
          color: 'var(--danger)',
        }}>
          <Icon size={24}>{I.alert}</Icon>
        </div>
        <Button size="lg" onClick={onRequestNewLink} style={{ justifyContent: 'center', width: '100%' }}>
          {t('auth.reset.requestNewLong')}
        </Button>
      </div>
    </AuthLayout>
  )
}

export function ResetPasswordPage({ token, onResetSuccess, onRequestNewLink }: ResetPasswordPageProps) {
  const { t } = useTranslation()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [tokenInvalid, setTokenInvalid] = useState(false)
  const [loading, setLoading] = useState(false)

  const checks = passwordPolicyChecks(password)

  if (!token || tokenInvalid) {
    return <InvalidTokenState onRequestNewLink={onRequestNewLink} />
  }

  const activeToken = token

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)

    if (!password || !confirmPassword) {
      setError(t('auth.reset.errors.missingFields'))
      return
    }
    if (password !== confirmPassword) {
      setError(t('auth.reset.errors.mismatch'))
      return
    }
    if (!passwordMeetsPolicy(checks)) {
      setError(t('auth.reset.errors.policy'))
      return
    }

    setLoading(true)
    try {
      const response = await confirmPasswordReset(activeToken, password)
      onResetSuccess(response)
    } catch (caught) {
      if (caught instanceof AuthApiError) {
        if (caught.code === 'INVALID_RESET_TOKEN') {
          setTokenInvalid(true)
        } else if (caught.code === 'PASSWORD_POLICY_FAILED') {
          setError(t('auth.reset.errors.policyServer'))
        } else {
          setError(t('auth.reset.errors.network'))
        }
      } else {
        setError(t('auth.reset.errors.network'))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout
      eyebrow={t('auth.reset.eyebrow')}
      title={t('auth.reset.title')}
      subtitle={t('auth.reset.subtitle')}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button type="button" onClick={onRequestNewLink} style={{ border: 'none', background: 'transparent', color: 'var(--brand-700)', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
            {t('auth.reset.needNewLink')}
          </button>
        </div>
      )}
    >
      <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 15 }}>
        <Field label={t('auth.reset.newPassword')} type="password" placeholder={t('auth.reset.newPasswordPlaceholder')} value={password} onChange={event => setPassword(event.target.value)} />
        <Field label={t('auth.reset.confirmPassword')} type="password" placeholder={t('auth.reset.confirmPasswordPlaceholder')} value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} />
        <PasswordChecklist checks={checks} />

        {error && (
          <div style={{ padding: '10px 12px', borderRadius: 10, background: 'var(--danger-100)', color: 'var(--danger)', fontSize: 13, lineHeight: 1.45 }}>
            {error}
          </div>
        )}

        <Button type="submit" size="lg" disabled={loading} style={{ justifyContent: 'center', width: '100%' }}>
          {loading ? t('auth.reset.submitting') : t('auth.reset.submit')}
        </Button>
      </form>
    </AuthLayout>
  )
}
